// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The event log.
 *
 * It exists because this tool makes a claim — that it reads two files you are
 * not allowed to upload and uploads neither — and a claim is worth more when
 * you can watch it being kept. Every pass over the training file is logged with
 * its throughput, and there is no line in here that names a network host,
 * because there is no request to name.
 *
 * The drawer is built ONCE and lives outside the re-rendered region, so the log
 * survives a re-render and its own open state is not reset by an unrelated
 * interaction.
 */

export type LogKind = 'info' | 'good' | 'warn' | 'bad';

interface Entry {
  readonly at: Date;
  readonly kind: LogKind;
  readonly message: string;
}

const entries: Entry[] = [];
let listEl: HTMLElement | null = null;
let drawerEl: HTMLElement | null = null;
let countEl: HTMLElement | null = null;
let toggleEl: HTMLButtonElement | null = null;

const MARKERS: Record<LogKind, string> = {
  info: '·',
  good: 'ok',
  warn: '!',
  bad: 'x',
};

function stamp(at: Date): string {
  return `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}:${String(
    at.getSeconds(),
  ).padStart(2, '0')}`;
}

function render(entry: Entry): void {
  if (!listEl) return;
  const line = document.createElement('div');
  line.className = `log-line log-${entry.kind}`;

  const time = document.createElement('span');
  time.className = 'log-time';
  time.textContent = stamp(entry.at);

  const marker = document.createElement('span');
  marker.className = 'log-marker';
  marker.textContent = MARKERS[entry.kind];

  const text = document.createElement('span');
  text.className = 'log-text';
  // textContent, always: a log line carries file names and parser errors,
  // neither of which evalspill wrote.
  text.textContent = entry.message;

  line.append(time, marker, text);
  listEl.appendChild(line);
  listEl.scrollTop = listEl.scrollHeight;
}

export function log(kind: LogKind, message: string): void {
  const entry = { at: new Date(), kind, message };
  entries.push(entry);
  render(entry);
  if (countEl) countEl.textContent = String(entries.length);
}

export function isOpen(): boolean {
  return drawerEl !== null && !drawerEl.hasAttribute('hidden');
}

export function setOpen(open: boolean): void {
  if (!drawerEl || !toggleEl) return;
  drawerEl.toggleAttribute('hidden', !open);
  toggleEl.setAttribute('aria-expanded', String(open));
}

export function logText(): string {
  return entries.map((e) => `[${stamp(e.at)}] ${MARKERS[e.kind]} ${e.message}`).join('\n');
}

/**
 * Attach the log toggle to `host`, building the drawer the first time.
 *
 * THIS IS CALLED FROM `header()`, WHICH RUNS ON EVERY RENDER, so it has to be
 * idempotent. A sibling tool's first version was not: it appended a fresh
 * drawer to `document.body` each time, three were live at once, the toggle
 * controlled the newest while `getElementById` returned the first, and the
 * drawer simply stopped opening after the first interaction. Nothing about that
 * is visible without counting the nodes.
 *
 * The toggle is the SAME node every time, merely re-parented into the rebuilt
 * nav — `appendChild` moves an existing element rather than copying it — so its
 * listener is bound once and the drawer it controls never changes.
 */
export function mountEventLog(host: HTMLElement): void {
  if (toggleEl && drawerEl) {
    host.appendChild(toggleEl);
    return;
  }

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'log-toggle';
  toggle.setAttribute('aria-expanded', 'false');
  toggle.setAttribute('aria-controls', 'event-log');

  const toggleLabel = document.createElement('span');
  toggleLabel.textContent = 'Log';
  const count = document.createElement('span');
  count.className = 'log-count';
  count.textContent = '0';
  toggle.append(toggleLabel, count);
  countEl = count;
  toggleEl = toggle;

  const drawer = document.createElement('aside');
  drawer.id = 'event-log';
  drawer.className = 'log-drawer';
  drawer.setAttribute('aria-label', 'Event log');
  drawer.hidden = true;
  drawerEl = drawer;

  const header = document.createElement('div');
  header.className = 'log-header';
  const title = document.createElement('h2');
  title.textContent = 'Event log';

  // The close control is INSIDE the drawer. On a phone the drawer covers the
  // toggle that opened it, so "tap the toggle again" is not an exit.
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'icon-btn';
  close.setAttribute('aria-label', 'Close the event log');
  close.textContent = '×';

  const copy = document.createElement('button');
  copy.type = 'button';
  copy.className = 'ghost-btn';
  copy.textContent = 'Copy';

  header.append(title, copy, close);

  const list = document.createElement('div');
  list.className = 'log-list';
  listEl = list;

  drawer.append(header, list);

  // One event family per control. Wiring both `click` and `touchstart` makes a
  // real tap fire twice — the drawer closes and instantly reopens, which reads
  // as a dead button and is invisible to a scripted `.click()`.
  toggle.addEventListener('click', () => setOpen(!isOpen()));
  close.addEventListener('click', () => {
    setOpen(false);
    toggle.focus();
  });
  copy.addEventListener('click', () => {
    void navigator.clipboard?.writeText(logText()).then(
      () => {
        copy.textContent = 'Copied';
        setTimeout(() => (copy.textContent = 'Copy'), 1400);
      },
      () => {
        copy.textContent = 'Copy failed';
        setTimeout(() => (copy.textContent = 'Copy'), 1400);
      },
    );
  });

  host.appendChild(toggle);
  document.body.appendChild(drawer);

  for (const entry of entries) render(entry);
  count.textContent = String(entries.length);
}
